// The handoff checklist in a Gate (PLAN D5; CR-059): his "Coordinator validates handoff; HR Operations completes HRIS
// entry", with his HR Operations folded into the Coordinator (DC-228; matrix "Validate/Complete"). Required items that
// are still open block Validate handoff; optional items never do. The Coordinator (and so the Recruiter) records items
// and validates, and the Admin does too, as its own act (no override path). Everyone else reads the gate with no action
// (matrix "View"). Confirming the HRIS entry is not an item: it is the HRIS card's act.

import { useState } from "react";
import {
  ACTION_LABELS, Button, CheckRow, Checklist, COMMON_LABELS, Flex, G1_LABELS, Gate, Input, REQUISITION_STATUS,
  ROLE_LABELS, Stack, formatDate, type GateRow,
} from "@/mc";
import { CLOSURE_LABELS, DATE_LABELS, ITEM_LABELS, unconfirmed } from "./labels";
import { slug } from "./slug";
import type { CloseoutApplication, ItemState, RunWrite } from "./types";

/** An item's name: his word where the glossary has it, the plan's pending word otherwise, the key as the last resort. */
export function itemLabel(key: string): string {
  switch (key) {
    case "signed_offer": return unconfirmed("signedOffer");
    case "background_check": return DATE_LABELS.backgroundCheck;
    case "start_date": return DATE_LABELS.startDate;
    case "position_no":
    case "position_number": return unconfirmed("positionNumber");
    case "manager": return ROLE_LABELS.hm;
    case "location": return ITEM_LABELS.location;
    default: return key;
  }
}

const isClosed = (item: ItemState) => item.state === "done" || item.state === "not_required";

/** One gate row per item: closed → ok; open and required → todo (blocks); open and optional → ok, marked Optional. */
export function gateRows(app: CloseoutApplication): GateRow[] {
  return app.checklist.map((item) => {
    const sub = item.state === "not_required"
      ? item.value_text ? `${G1_LABELS.notRequired} · ${item.value_text}` : G1_LABELS.notRequired
      : item.value_date ? formatDate(item.value_date) : item.value_text ?? (item.required ? COMMON_LABELS.required : COMMON_LABELS.optional);
    return {
      status: isClosed(item) || !item.required ? "ok" : "todo",
      text: itemLabel(item.key),
      sub,
      hs: `close.item.${slug(app.application_id)}.${slug(item.key)}`,
    } satisfies GateRow;
  });
}

function ItemControl({ app, item, runWrite, busy, setBusy }: {
  app: CloseoutApplication; item: ItemState; runWrite: RunWrite;
  busy: boolean; setBusy: (busy: boolean) => void;
}) {
  const [date, setDate] = useState(item.value_date ?? "");
  const id = app.application_id;
  const record = async (state: "done" | "not_required") => {
    setBusy(true);
    try {
      await runWrite({ tool: "handoff.record_item", input: { application_id: id, item_key: item.key, state, value_date: state === "done" ? date || null : null } });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Flex>
      <Input sm type="date" value={date} onChange={(event) => setDate(event.target.value)} hs={`close.item-date.${slug(id)}.${slug(item.key)}`} />
      <Button sm busy={busy} onClick={() => record("done")} hs={`close.item-save.${slug(id)}.${slug(item.key)}`}
        blocked={date ? undefined : { reason: COMMON_LABELS.required }}>
        {ACTION_LABELS.complete}
      </Button>
      {item.required ? null : (
        <Button sm tone="ghost" busy={busy} onClick={() => record("not_required")}>
          {G1_LABELS.notRequired}
        </Button>
      )}
    </Flex>
  );
}

export interface HandoffCardProps {
  app: CloseoutApplication;
  runWrite: RunWrite;
  readOnly?: boolean | undefined;
}

export function HandoffCard({ app, runWrite, readOnly }: HandoffCardProps) {
  const id = app.application_id;
  const [busy, setBusy] = useState(false);
  const validated = !!app.ready_for_hris_at;
  const mayValidate = !readOnly && !validated && app.can.validate;
  const mayRecord = !readOnly && !validated && app.can.record_item;
  const open = app.checklist.filter((item) => !isClosed(item) && item.source !== "derived");

  const validate = async () => {
    setBusy(true);
    try {
      await runWrite({ tool: "handoff.validate", input: { application_id: id } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack>
      <Gate
        title={CLOSURE_LABELS.handoffChecklist}
        verdict={validated ? REQUISITION_STATUS.readyForHris : undefined}
        rows={gateRows(app)}
        ready={mayValidate && gateRows(app).every((row) => row.status === "ok")}
        mode={mayValidate ? "edit" : "view"}
        action={mayValidate ? {
          label: ACTION_LABELS.validateHandoff,
          onClick: validate, busy, hs: `close.validate.${slug(id)}`,
        } : undefined}
      />
      {mayRecord && open.length ? (
        <Checklist>
          {open.map((item) => (
            <CheckRow key={item.key} status={item.required ? "todo" : "ok"}
              control={<ItemControl app={app} item={item} runWrite={runWrite} busy={busy} setBusy={setBusy} />}>
              {itemLabel(item.key)}
            </CheckRow>
          ))}
        </Checklist>
      ) : null}
    </Stack>
  );
}
